import React, {useCallback, useMemo} from 'react';
import classnames from 'classnames';
import {List} from 'immutable';
import {callTargetedAction, setUpStore} from 'client/framework';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {Fieldset} from '@albert-io/atomic';
import type {QuestionModelV3} from '@albert-io/models';
import {useShallow} from 'zustand/react/shallow';
import {
  useQuestionTranslationStore,
  NoTranslationWarningHeader,
  btnDisabledWarning,
  translatedInstructions
} from 'components/QuestionTypes/shared/TranslationSection';

import snippetSelectionQuestionActions from './SnippetSelectionQuestion.actions';
import SnippetSelectionQuestionStore from 'client/components/QuestionTypes/SnippetSelection/SnippetSelectionQuestion.store';

import './snippet-selection-question.scss';

interface SnippetSelectionInputAreaProps {
  questionId: string;
  question: QuestionModelV3;
  storeName: string;
  isGuessSubmitted: boolean;
}

/*
old propTypes
  static propTypes = {
    questionId: PropTypes.string,
    question: PropTypes.instanceOf(getModelForResourceType('question_v3')),
    storeName: PropTypes.string.isRequired,
    isGuessSubmitted: PropTypes.bool
  };
*/

const SnippetSelectionInputArea = ({
  questionId,
  question,
  storeName,
  isGuessSubmitted
}: SnippetSelectionInputAreaProps) => {
  // Translation info
  const {translatedQuestionInfo, showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      translatedQuestionInfo: state.translatedQuestionInfo,
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  const {prompt, options} = useMemo(() => {
    if (showTranslations && translatedQuestionInfo) {
      return {
        prompt: translatedQuestionInfo.snippet_prompt,
        options: formatOptions(translatedQuestionInfo.options)
      };
    }
    return {
      prompt: question.getSnippetPrompt(),
      options: question.getOptions()
    };
  }, [translatedQuestionInfo, showTranslations, question]);

  // end translation info

  const store = useMemo(() => {
    // `setUpStore` is typed as returning the base `Store`. `hasResponse` is a
    // dynamic data field that may be present on the underlying snippet-selection
    // store instance, so surface it as an optional property for narrowing below.
    return setUpStore(SnippetSelectionQuestionStore, storeName, questionId) as ReturnType<
      typeof setUpStore
    > & {hasResponse?: boolean};
  }, [storeName, questionId]);

  const onOptionClick = useCallback(
    (optionId: string) => {
      if (isGuessSubmitted) return;
      callTargetedAction({
        name: snippetSelectionQuestionActions.TOGGLE_OPTION,
        targetStore: store?.getName(),
        payload: optionId
      });
    },
    [isGuessSubmitted, store]
  );

  const inputAreaClassNames = useMemo(() => {
    return classnames('snippet-selection-question__input-area', {
      'snippet-selection-question__input-area--interactive': !(
        isGuessSubmitted ||
        store?.hasResponse ||
        showTranslations
      )
    });
  }, [isGuessSubmitted, store, showTranslations]);

  return (
    <>
      {showTranslations && (
        <NoTranslationWarningHeader className='mb-x4' text={btnDisabledWarning} />
      )}
      <Fieldset
        legend={showTranslations ? translatedInstructions.selectMultiple : 'Select answer(s) below'}
      >
        <div className='snippet-selection-question mb-x2'>
          <MarkdownRendererV2
            className={inputAreaClassNames}
            isGuessSubmitted={isGuessSubmitted}
            text={prompt}
            snippetSelectionData={{
              isExplanation: isGuessSubmitted,
              options,
              storeName: store?.getName(),
              onOptionClick
            }}
            question={question}
            isBeingTranslated={showTranslations}
          />
        </div>
      </Fieldset>
    </>
  );
};

export {SnippetSelectionInputArea};

const formatOptions = (options: any | null) => {
  const formattedOptions =
    options === null
      ? []
      : options.map((option) => {
          return {
            ...option,
            getId: () => option.id
          };
        });

  return List(formattedOptions);
};
